import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/auth/apiClient';
import { queryKeys } from './queryKeys';
import type { Traeger, TraegerPatch, TraegerWrite, UUID } from './types';

export function useTraegerList() {
  return useQuery({
    queryKey: queryKeys.traeger.list(),
    queryFn: async (): Promise<Traeger[]> => {
      const res = await apiClient.get<Traeger[]>('/traeger');
      return res.data;
    },
    staleTime: 5 * 60_000,
  });
}

export function useTraeger(id: UUID | null) {
  return useQuery({
    queryKey: id ? queryKeys.traeger.detail(id) : ['traeger', 'detail', 'none'],
    queryFn: async (): Promise<Traeger> => {
      if (!id) throw new Error('id required');
      const res = await apiClient.get<Traeger>(`/traeger/${id}`);
      return res.data;
    },
    enabled: !!id,
  });
}

export function useCreateTraeger() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: TraegerWrite): Promise<Traeger> => {
      const res = await apiClient.post<Traeger>('/traeger', body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.traeger.all });
    },
  });
}

export function useUpdateTraeger() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: UUID; patch: TraegerPatch }): Promise<Traeger> => {
      const res = await apiClient.patch<Traeger>(`/traeger/${vars.id}`, vars.patch);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.traeger.all });
    },
  });
}

export function useDeleteTraeger() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      id: UUID;
      confirm_name: string;
      reason: string;
    }): Promise<void> => {
      await apiClient.delete(`/traeger/${vars.id}`, {
        params: { confirm_name: vars.confirm_name, reason: vars.reason },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.traeger.all });
    },
  });
}
